import { owaComputedFn } from "owa-computed-fn";
import { logUsage } from "owa-analytics";
import type { ConditionalFormattingEvaluationContext } from "./isConditionalFormattingConditionSatisfied";
import { getApplicableConditionalFormattingRulesByMailbox, getKeyForRowIdInRowToModificationMap, getConditionalFormattingModificationForRow, } from "owa-conditional-formatting";
import type { ConditionalFormattingModification } from "owa-conditional-formatting";
import type DistinguishedFolderIdName from "owa-service/lib/contract/DistinguishedFolderIdName";
import getConditionalFormattingModificationsToApplyOnRow from "./getConditionalFormattingModificationsToApplyOnRow";
import isArchiveSupported from "./isArchiveSupported";
import isConsumer from "owa-session-store/lib/utils/isConsumer";
import loc from "owa-localize";
import { noSubject } from "owa-locstrings/lib/strings/nosubject.locstring.json";
import { unknownSenderOrRecipient } from "owa-people-strings/lib/strings/unknownsenderorrecipient.locstring.json";
import shouldAllowDelete from "owa-mail-filterable-menu-behavior/lib/utils/shouldAllowDelete";
import getItemClassIcon from "owa-mail-list-actions/lib/utils/conversationProperty/getItemClassIcon";
import doesConversationHaveDrafts from "owa-mail-list-actions/lib/utils/doesConversationHaveDrafts";
import isMessageUnauthenticated from "owa-mail-sender-persona-view/lib/utils/isMessageUnauthenticated";
import { isSuggestedUnsubscribeItem } from "owa-mail-suggested-unsubscribe-store";
import type { ClientItem } from "owa-mail-store";
import mailStore from "owa-mail-store/lib/store/Store";
import { externalInOutlookEnabled } from "owa-nonboot-userconfiguration-manager";
import type ReactListViewType from "owa-service/lib/contract/ReactListViewType";
import getUserConfiguration from "owa-session-store/lib/actions/getUserConfiguration";
import folderIdToName from "owa-session-store/lib/utils/folderIdToName";
import getSmimeType, { getSmimeTypeForConversation } from "owa-smime/lib/utils/getSmimeType";
import * as trace from "owa-trace";
import type { MailListItemDataProps, MailListTableProps } from "owa-mail-list-item-shared";
import getCleanPreview from "owa-mail-list-item-shared/lib/utils/getCleanPreview";
import { isSecondLevelExpanded, isFirstLevelExpanded, } from "owa-mail-list-store/lib/selectors/isConversationExpanded";
import type Message from "owa-service/lib/contract/Message";
import type { TableView, MailFolderTableQuery, TableQuery, ConversationItem, } from "owa-mail-list-store";
import { getUserCategoryName, getTableConversationRelation, getMeetingMessageItems, isSearchFolderScenario, isItemOfMessageType, getStore as getListViewStore, MailRowDataPropertyGetter, type TableQueryType, type SortColumn, getRowKeyMailboxInfoIfUnifiedInboxOrMultiAccountSearch, isCopilotResultsTable } from "owa-mail-list-store";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import type { MailboxInfo } from "owa-client-types";
import getSenderNameForDisplay from "owa-mail-store/lib/utils/getSenderNameForDisplay";
import { isMeetingRequest, isMeetingDelegated } from "owa-meeting-message-utils";
import { queueCacheUpdate as conditionalFormattingQueue } from "./conditionalFormattingProcessor";
import type MeetingRequestMessageType from "owa-service/lib/contract/MeetingRequestMessageType";
import getCopilotPrioritizeSettings from "owa-mail-copilot-settings/lib/selectors/getCopilotPrioritizeSettings";
import { isCopilotHighPriority, isCopilotLowPriority } from "owa-mail-copilot-inbox-shared";
import { isPrioritizeOnDeliveryEnabled } from "owa-copilot-settings-utils";
import { isProactiveDraft } from "owa-proactive-drafts-shared";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { isDraftsMeaningfulSaveTimeEnabled } from "owa-mail-triage-common";

const emptyConditionalFormattingModifications: ConditionalFormattingModification[] = [];
Object.freeze(emptyConditionalFormattingModifications);

const getMailListItemDataProps: (rowKey: string, mailListTableProps: MailListTableProps) => MailListItemDataProps | null = owaComputedFn((rowKey: string, mailListTableProps: MailListTableProps): MailListItemDataProps | null => {
    const tableView = getListViewStore().tableViews.get(mailListTableProps.tableViewId);
    if (!tableView) {
        return null;
    }
    
    const listViewType = tableView.tableQuery.listViewType;
    
    // VSO 20477: Combine getMailListItemPropsForConversation and getMailListItemPropsForItem
    switch (listViewType) {
        case 0:
            return getMailListItemPropsForConversation(rowKey, tableView, mailListTableProps);
        case 1:
            return getMailListItemPropsForItem(rowKey, tableView, mailListTableProps);
        
        default:
            trace.trace.warn("Not supported listViewType:" + listViewType);
            return null;
    }
});

function getMailListItemPropsForItem(rowKey: string, tableView: TableView, mailListTableProps: MailListTableProps): MailListItemDataProps | null {
    if (!rowKey) {
        throw new Error("getMailListItemPropsForItem: ItemId should be present for given rowKey");
    }
    const tableViewMailboxInfo = getMailboxInfo(tableView);
    const mailboxInfo = getRowKeyMailboxInfoIfUnifiedInboxOrMultiAccountSearch(tableViewMailboxInfo, tableView, rowKey);
    const rowId = MailRowDataPropertyGetter.getRowIdString(rowKey, tableView);
    const item: ClientItem | undefined = mailStore().items.get(rowId);
    
    if (!item) {
        return null;
    }
    
    let message;
    
    if (isItemOfMessageType(item)) {
        message = item as Message;
    }
    else {
        message = null;
    }
    
    const userConfiguration = getUserConfiguration();
    
    // Calculate first two line displays
    const isItemPinned = MailRowDataPropertyGetter.getIsPinned(rowKey, tableView);
    const subject = item.Subject ? item.Subject : loc(noSubject);
    const folderId = tableView.tableQuery.folderId;
    const parentFolderId = item.ParentFolderId?.Id;
    const parentFolderDisplayName = item.ParentFolderDisplayName;
    const folderName = folderIdToName(folderId, true /* shouldSearchShared */);
    const parentFolderName = parentFolderId && folderIdToName(parentFolderId, true /* shouldSearchShared */);
    const isDraftsFolder = folderName == "drafts";
    const isItemInDraftsFolder = isDraftsFolder || parentFolderName == "drafts";
    const isItemInSentItemsFolder = folderName == "sentitems" || parentFolderName == "sentitems";
    const isItemInNotesFolder = folderName == "notes" || parentFolderName == "notes";
    const isItemInOutboxFolder = folderName == "outbox" || parentFolderName == "outbox";
    const isItemInArchiveFolder = folderName == "archive" || parentFolderName == "archive";
    const isItemInJunkFolder = folderName == "junkemail" || parentFolderName == "junkemail";
    const mailboxGuids = MailRowDataPropertyGetter.getMailboxGuids(rowKey, tableView);
    const isGroupItem = tableView.tableQuery.type == 2;
    const isComplete = item.Flag ? item.Flag.FlagStatus === "Complete" : false; // item.Flag could be undefined in search results
    const isFlagged = item.Flag ? item.Flag.FlagStatus === "Flagged" : false; // item.Flag could be undefined in search results
    const flagDueDate = item.Flag?.DueDate && !item.Flag.DueDate.startsWith("9999") ? item.Flag.DueDate : null; // Service returns '9999' when there is no due date, so set it as null
    
    let folderNameToUseForArchiveSupportCheck: DistinguishedFolderIdName = folderName;
    if (isCopilotResultsTable(tableView.tableQuery)) {
        folderNameToUseForArchiveSupportCheck = parentFolderName || "none";
    }
    
    let senderName = message?.From?.Mailbox?.Name || "";
    const senderEmailAddress = message?.From?.Mailbox?.EmailAddress ?? "";
    const categoriesToShow = getCategoriesToShow(tableView, item.Categories);
    
    let conditionalFormattingModifications: ConditionalFormattingModification[] = [];
    const applicableRules = getApplicableConditionalFormattingRulesByMailbox(mailboxInfo, "Mail");
    
    const messageSize = convertToMB(message?.Size ?? null);
    if (applicableRules.length > 0) {
        const messageToMe = message?.MessageToMe;
        const messageCcMe = message?.MessageCcMe;
        const readKey = message?.IsRead === true ? "R" : message?.IsRead === false ? "U" : "";
        const existingKeyForRowId = getKeyForRowIdInRowToModificationMap(rowId, mailboxInfo, "Mail") ?? "";
        const newKeyForRowId = rowId +
            subject +
            (messageToMe !== undefined && messageToMe !== null ? messageToMe.toString() : "") +
            (messageCcMe !== undefined && messageCcMe !== null ? messageCcMe.toString() : "") +
            readKey +
            (messageSize ? messageSize?.toString() : "");
        
        // Check if we have a cached value that's still valid
        if (existingKeyForRowId === newKeyForRowId) {
            // Cache is valid, use existing modifications
            conditionalFormattingModifications = getConditionalFormattingModificationForRow(existingKeyForRowId, mailboxInfo, "Mail");
        }
        else {
            // Defer CF evaluation to avoid blocking render
            conditionalFormattingModifications = [];
            
            // Cache is stale, but DON'T mutate during render
            // Just compute the new value
            const conditionalCtx = {
                subject,
                toMe: messageToMe,
                ccMe: messageCcMe,
                senderEmail: senderEmailAddress,
                mentionsMe: !!item.MentionedMe,
                isRead: message?.IsRead ?? undefined,
                hasAttachments: !!item.HasAttachments,
                messageSize,
                importance: message?.Importance ?? null,
                isCopilotPrioritizedHigh: isCopilotHighPriority(message?.CopilotInboxScore),
                categories: categoriesToShow,
                isFlagged,
                isCompleted: isComplete,
            } satisfies ConditionalFormattingEvaluationContext;
            
            setTimeout(() => {
                const modifications = getConditionalFormattingModificationsToApplyOnRow(applicableRules, conditionalCtx);
                conditionalFormattingQueue({
                    oldKey: existingKeyForRowId,
                    newKey: newKeyForRowId,
                    modifications,
                    mailboxInfo,
                });
            }, 0);
        }
    }
    
    //Canceled appointments (type IPM.Appointment) can have an empty sender name.
    //So we will show the recipients and senders. In parity.
    if (!senderName && item.ItemClass && isItemOfAppointmentType(item.ItemClass)) {
        senderName = item.DisplayTo ?? "";
    }
    
    // Query-on-type rows provide senders, not recipients, even when scoped to Sent Items.
    const isQueryOnTypeRow = tableView.recentRowKeys?.includes(rowKey) === true ||
        tableView.moreResultsRowKeys?.includes(rowKey) === true;
    const { sendersOrRecipients, singleLastSenderSMTP } = getSendersOrRecipients([item.DisplayTo ?? ""], [senderName], MailRowDataPropertyGetter.getLastSenderSMTP(rowKey, tableView), isItemInSentItemsFolder, isItemInDraftsFolder, isItemInOutboxFolder, mailListTableProps.isSearchNotInDraftOrSentItemFolder || isQueryOnTypeRow, tableView.tableQuery);
    let firstLineText: string;
    let firstLineTooltipText: string | null = null;
    let secondLineText: string;
    let secondLineTooltipText: string | null = null;
    
    let unreadCount = 0;
    if (message) {
        unreadCount = message.IsRead ? 0 : 1;
    }
    
    // Show pinned items rows in condensed view when
    // table supports it and
    // item is pinned
    // item is already read
    const showCondensedView = mailListTableProps.showCondensedPinnedRows && isItemPinned && unreadCount === 0;
    
    const shouldShowSenderOnTop = userConfiguration?.UserOptions?.ShowSenderOnTopInListView ||
        mailListTableProps.isSingleLine ||
        isDraftsFolder; // When in Drafts folder we always show recipients on the first line irrespective of user's settings
    
    if (shouldShowSenderOnTop) {
        firstLineText = sendersOrRecipients;
        firstLineTooltipText = singleLastSenderSMTP;
        secondLineText = subject;
    }
    else {
        secondLineText = sendersOrRecipients;
        secondLineTooltipText = singleLastSenderSMTP;
        firstLineText = subject;
        firstLineTooltipText = null;
    }
    
    const shouldSuggestUnsubscribe = shouldShowSuggestedUnsubscribe(rowId, tableView, mailboxInfo);
    
    const copilotInboxScore = message?.CopilotInboxScore ?? -1;
    const copilotPrioritizeHeadline = message?.CopilotInboxHeadline ?? "";
    const showCopilotPrioritizeHeadline = shouldShowCopilotPrioritizeHeadline(copilotInboxScore, copilotPrioritizeHeadline, tableView, mailboxInfo);
    
    // Do not populate preview text if preview is disabled
    const previewText = mailListTableProps.showPreviewText
        ? showCopilotPrioritizeHeadline
            ? copilotPrioritizeHeadline
            : getCleanPreview(item.Preview ?? "")
        : "";
    
    const thirdLineText = previewText;
    
    // We make the assumption that if a ReceivedOrRenewTime != DateTimeReceived the item must either be pinned or snoozed.
    // If the item has a return time or is NOT pinned and has the above inequality, we will mark the item as snoozed.
    const isSnoozed = mailListTableProps.supportsSnooze &&
        !!(!!item.ReturnTime ||
            (!isItemPinned &&
                item.ReceivedOrRenewTime &&
                item.DateTimeReceived &&
                !isTimestampEqual(item.ReceivedOrRenewTime, item.DateTimeReceived)));
    
    const meetingMessageItem = item?.ItemId?.Id && getMeetingMessageItems(item.ItemId.Id);
    const hasDelegateMeeting = meetingMessageItem ? isMeetingDelegated(meetingMessageItem) : false;
    
    const itemClassIcon = getItemClassIcon(item.ItemClass ? [item.ItemClass] : undefined, item.IconIndex ?? "Default", false /* hasIrm */, hasDelegateMeeting);
    
    const showAttachmentPreviews: boolean = !!userConfiguration?.UserOptions?.ShowInlinePreviews && !!item.shouldShowAttachmentPreviews;
    
    const meetingStartTime = isMeetingRequest(item?.ItemClass)
        ? (item as MeetingRequestMessageType)?.Start
        : undefined;
    
    return {
        canDelete: shouldAllowDelete(tableView, rowKey),
        categories: categoriesToShow,
        conversationId: item.ConversationId?.Id ?? "",
        effectiveMentioned: !!item.MentionedMe,
        firstLineText,
        firstLineTooltipText,
        hasAttachment: !!item.HasAttachments,
        importance: item.Importance ?? "Normal",
        isComplete,
        isFlagged,
        isPinned: isItemPinned,
        isSelected: tableView.selectedRowKeys.has(rowKey) && !tableView.headerToFocus,
        itemClassIcon,
        lastDeliveryTimestamp: getDraftAwareDisplayTimestamp(item, isItemInDraftsFolder, isDraftsMeaningfulSaveTimeEnabled(mailboxInfo)),
        lastSender: message?.From,
        latestItemId: item.ItemId?.Id ?? "",
        parentFolderId: parentFolderId ?? "",
        parentFolderDisplayName: parentFolderDisplayName ?? "",
        rowId,
        rowKey,
        secondLineText,
        secondLineTooltipText,
        showAttachmentPreview: showAttachmentPreviews,
        isUnauthenticatedSender: !!message && !!isMessageUnauthenticated(message),
        bulkCategory: message?.MessageBulkCategory ?? "None",
        isSnoozed,
        returnTime: getItemReturnTime(item, isSnoozed),
        showCondensedView,
        showDraftsIndicator: !!item.IsDraft,
        subject,
        thirdLineText,
        unreadCount,
        smimeType: getSmimeType(item),
        sendersOrRecipients,
        shouldShowTwisty: false,
        isInNotesFolder: isItemInNotesFolder,
        isInOutboxFolder: isItemInOutboxFolder,
        isInArchiveFolder: isItemInArchiveFolder,
        isInJunkFolder: isItemInJunkFolder,
        shouldShowRSVP: !!item.shouldShowRSVP,
        shouldShowSenderOnTop,
        shouldShowExternalLabel: !!item.IsExternalSender && externalInOutlookEnabled(mailboxInfo),
        shouldSuggestUnsubscribe,
        hasSharepointLink: !!item.HasProcessedSharepointLink,
        canArchive: isArchiveSupported(tableView, mailboxInfo, folderNameToUseForArchiveSupportCheck),
        canPin: canPin(tableView, mailListTableProps.supportsPinning, parentFolderId ?? ""),
        isInGroup: isGroupItem,
        mailboxGuids,
        conditionalFormattingModifications: isGlobalFeatureEnabled("mon-mail-list-focus-computed") &&
            conditionalFormattingModifications.length === 0
            ? emptyConditionalFormattingModifications
            : conditionalFormattingModifications,
        meetingStartTime,
        copilotInboxScore,
        isReminderSet: !!item.Flag?.ReminderIsSet,
        isScheduledSend: !!item.DeferredSendTime || !!item.isDelaySend,
        // Copilot draft detection
        isProactiveDraft: isProactiveDraft(item),
        mailboxInfo,
        messageSizeBytes: message?.Size ?? null,
        flagDueDate,
    };
}

function getMailListItemPropsForConversation(rowKey: string, tableView: TableView, mailListTableProps: MailListTableProps): MailListItemDataProps | null {
    const tableConversationRelation = getTableConversationRelation(rowKey, tableView.id);
    if (!tableConversationRelation) {
        trace.trace.warn("tableConversationRelation not found in store.");
        return null;
    }
    const rowId = tableConversationRelation.id;
    const conversationItem: ConversationItem | undefined = getListViewStore().conversationItems.get(rowId);
    const userConfiguration = getUserConfiguration();
    const tableViewMailboxInfo = getMailboxInfo(tableView);
    const mailboxInfo = getRowKeyMailboxInfoIfUnifiedInboxOrMultiAccountSearch(tableViewMailboxInfo, tableView, rowKey);
    
    if (!conversationItem) {
        // Converting exception to trace error here to prevent app crash.
        // VSO 25058: Investigate why conversationItem not found in store in getMailListItemDataProps
        const sort = (tableView.tableQuery as MailFolderTableQuery).sortBy?.sortColumn;
        
        logUsage("ConversationNotFound", mailboxInfo, { sort });
        trace.trace.warn("getProps Conversation not found in store. sortColumn " + sort);
        return null;
    }
    
    // Get item status
    const isComplete = tableConversationRelation.flagStatus === "Complete";
    const isPinned = MailRowDataPropertyGetter.getIsPinned(rowKey, tableView);
    const mailboxGuids = MailRowDataPropertyGetter.getMailboxGuids(rowKey, tableView);
    const isFlagged = tableConversationRelation.flagStatus === "Flagged";
    const flagDueDate = tableConversationRelation.flagDueDate;
    const effectiveMentioned = tableConversationRelation.effectiveMentioned;
    
    // Calculate first two line displays
    const subject = conversationItem.subject ? conversationItem.subject : loc(noSubject);
    const parentFolderId = tableConversationRelation.parentFolderId;
    const folderName = folderIdToName(tableView.tableQuery.folderId, true /* shouldSearchShared */);
    const parentFolderName = parentFolderId && folderIdToName(parentFolderId, true /* shouldSearchShared */);
    const isDraftsFolder = folderName == "drafts";
    const isItemInDraftsFolder = isDraftsFolder || parentFolderName == "drafts";
    const isItemInSentItemsFolder = folderName == "sentitems" || parentFolderName == "sentitems";
    const isItemInNotesFolder = folderName == "notes" || parentFolderName == "notes";
    const isItemInOutboxFolder = folderName == "outbox" || parentFolderName == "outbox";
    const isItemInArchiveFolder = folderName == "archive" || parentFolderName == "archive";
    const isItemInJunkFoler = folderName == "junkemail" || parentFolderName == "junkemail";
    const isGroupItem = tableView.tableQuery.type == 2;
    const lastSenderEmailAddress = tableConversationRelation.lastSender?.Mailbox?.EmailAddress ?? "";
    const unreadCount = tableConversationRelation.unreadCount;
    const categoriesToShow = getCategoriesToShow(tableView, tableConversationRelation.categories);
    
    let conditionalFormattingModifications: ConditionalFormattingModification[] = [];
    const applicableRules = getApplicableConditionalFormattingRulesByMailbox(mailboxInfo, "Mail");
    
    const messageSize = convertToMB(conversationItem.size);
    if (applicableRules.length > 0) {
        const isRead = unreadCount > 0 ? false : true;
        const conversationToMe = tableConversationRelation.conversationToMe;
        const conversationCcMe = tableConversationRelation.conversationCcMe;
        const readKey = isRead ? "R" : "U";
        const existingKeyForRowId = getKeyForRowIdInRowToModificationMap(rowId, mailboxInfo, "Mail") ?? "";
        const newKeyForRowId = rowId +
            subject +
            (conversationToMe !== undefined && conversationToMe !== null
                ? conversationToMe.toString()
                : "") +
            (conversationCcMe !== undefined && conversationCcMe !== null
                ? conversationCcMe.toString()
                : "") +
            readKey +
            (messageSize ? messageSize?.toString() : "");
        
        // Check if we have a cached value that's still valid
        if (existingKeyForRowId === newKeyForRowId) {
            // Cache is valid, use existing modifications
            conditionalFormattingModifications = getConditionalFormattingModificationForRow(existingKeyForRowId, mailboxInfo, "Mail");
        }
        else {
            // Defer CF evaluation to avoid blocking render
            conditionalFormattingModifications = [];
            
            // Cache is stale, but DON'T mutate during render
            // Just compute the new value
            const conditionalCtx = {
                subject,
                toMe: conversationToMe,
                ccMe: conversationCcMe,
                senderEmail: lastSenderEmailAddress,
                mentionsMe: effectiveMentioned,
                isRead,
                hasAttachments: tableConversationRelation.hasAttachments,
                messageSize,
                importance: tableConversationRelation.importance,
                isCopilotPrioritizedHigh: isCopilotHighPriority(tableConversationRelation.copilotInboxScore),
                categories: categoriesToShow,
                isFlagged,
                isCompleted: isComplete,
            } satisfies ConditionalFormattingEvaluationContext;
            
            setTimeout(() => {
                const modifications = getConditionalFormattingModificationsToApplyOnRow(applicableRules, conditionalCtx);
                conditionalFormattingQueue({
                    oldKey: existingKeyForRowId,
                    newKey: newKeyForRowId,
                    modifications,
                    mailboxInfo,
                });
            }, 0);
        }
    }
    
    const { sendersOrRecipients, singleLastSenderSMTP } = getSendersOrRecipients(tableConversationRelation.uniqueRecipients, tableConversationRelation.uniqueSenders, MailRowDataPropertyGetter.getLastSenderSMTP(rowKey, tableView), isItemInSentItemsFolder, isItemInDraftsFolder, isItemInOutboxFolder, mailListTableProps.isSearchNotInDraftOrSentItemFolder, tableView.tableQuery);
    // Show conversation row in condensed view when
    // table supports condensed view
    // they are pinned and
    // not expanded
    // items are all read
    const showCondensedView = mailListTableProps.showCondensedPinnedRows &&
        isPinned &&
        !(isSecondLevelExpanded(rowKey) || isFirstLevelExpanded(rowKey)) &&
        unreadCount === 0;
    
    const shouldShowSenderOnTop = userConfiguration?.UserOptions?.ShowSenderOnTopInListView ||
        mailListTableProps.isSingleLine ||
        isDraftsFolder; // When in Drafts folder we always show recipients on the first line irrespective of user's settings
    
    let firstLineText: string;
    let firstLineTooltipText: string | null = null;
    let secondLineText: string;
    let secondLineTooltipText: string | null = null;
    
    if (shouldShowSenderOnTop) {
        firstLineText = sendersOrRecipients;
        firstLineTooltipText = singleLastSenderSMTP;
        secondLineText = subject;
    }
    else {
        secondLineText = sendersOrRecipients;
        secondLineTooltipText = singleLastSenderSMTP;
        firstLineText = subject;
    }
    
    const shouldSuggestUnsubscribe = shouldShowSuggestedUnsubscribe(rowId, tableView, mailboxInfo);
    
    const copilotInboxScore = tableConversationRelation.copilotInboxScore;
    const copilotPrioritizeHeadline = tableConversationRelation.copilotInboxHeadline ?? "";
    const showCopilotPrioritizeHeadline = shouldShowCopilotPrioritizeHeadline(copilotInboxScore, copilotPrioritizeHeadline, tableView, mailboxInfo);
    
    // Do not populate preview text if preview is disabled
    const previewText = mailListTableProps.showPreviewText
        ? showCopilotPrioritizeHeadline
            ? copilotPrioritizeHeadline
            : getCleanPreview(tableConversationRelation.previewText)
        : "";
    
    const thirdLineText = previewText;
    
    // Add [Drafts] indicator if the conversation has any drafts
    const showDraftsIndicator: boolean = doesConversationHaveDrafts(tableConversationRelation) || isItemInDraftsFolder;
    
    // Check if conversation has scheduled send or proactive draft
    let isScheduledSend = false;
    let isProactiveDraftItem = false;
    if (showDraftsIndicator &&
        tableConversationRelation.draftItemIds &&
        tableConversationRelation.draftItemIds.length > 0) {
        // Check the first draft item
        const firstDraftItemId = tableConversationRelation.draftItemIds[0];
        const firstDraftItem = mailStore().items.get(firstDraftItemId);
        if (firstDraftItem) {
            // Draft item is loaded - check its properties
            if (firstDraftItem.IsDraft) {
                isScheduledSend = !!firstDraftItem.DeferredSendTime || !!firstDraftItem.isDelaySend;
                isProactiveDraftItem = isProactiveDraft(firstDraftItem);
            }
        }
        else if (isGlobalFeatureEnabled("mon-copilot-proactive-drafts-view-debug")) {
            // Debug override: treat any unloaded draft as proactive.
            isProactiveDraftItem = true;
        }
    }
    
    const showAttachmentPreviews = !!userConfiguration?.UserOptions?.ShowInlinePreviews &&
        conversationItem.shouldShowAttachmentPreviews;
    
    // We make the assumption that if a lastDeliveryOrRenewTimeStamp != lastDeliveryTimeStamp the conversation must either be pinned or snoozed.
    // If the conversation has a return time or is NOT pinned and has the above inequality, we will mark the conversation as snoozed.
    const isSnoozed = mailListTableProps.supportsSnooze &&
        (!!tableConversationRelation.returnTime ||
            (!isPinned &&
                !isTimestampEqual(tableConversationRelation.lastDeliveryOrRenewTimeStamp, tableConversationRelation.lastDeliveryTimeStamp)));
    
    const meetingMessageItem = getMeetingMessageItems(tableConversationRelation.itemIds[0]);
    const meetingStartTime = isMeetingRequest(meetingMessageItem?.ItemClass)
        ? (meetingMessageItem as MeetingRequestMessageType)?.Start
        : undefined;
    const hasDelegateMeeting = meetingMessageItem ? isMeetingDelegated(meetingMessageItem) : false;
    
    const itemClassIcon = getItemClassIcon(tableConversationRelation.itemClasses, tableConversationRelation.iconIndex, tableConversationRelation.hasIrm, hasDelegateMeeting);
    
    return {
        rowKey,
        rowId,
        conversationId: rowId,
        firstLineText,
        firstLineTooltipText,
        secondLineText,
        secondLineTooltipText,
        thirdLineText,
        isComplete,
        isFlagged,
        isPinned,
        isSelected: tableView.selectedRowKeys.has(rowKey) && !tableView.headerToFocus,
        canDelete: shouldAllowDelete(tableView, rowKey),
        unreadCount,
        subject,
        categories: categoriesToShow,
        lastSender: tableConversationRelation.lastSender,
        latestItemId: tableConversationRelation.itemIds[0],
        lastDeliveryTimestamp: tableConversationRelation.lastDeliveryTimeStamp,
        itemClassIcon,
        importance: tableConversationRelation.importance,
        hasAttachment: tableConversationRelation.hasAttachments,
        showDraftsIndicator,
        showAttachmentPreview: showAttachmentPreviews,
        effectiveMentioned,
        parentFolderDisplayName: "",
        parentFolderId,
        isUnauthenticatedSender: !!conversationItem.conversationAntispamUnauthenticatedSender,
        bulkCategory: conversationItem.conversationBulkCategory ?? "None",
        isSnoozed,
        returnTime: getTableConversationRelationReturnTime(tableConversationRelation, isSnoozed),
        showCondensedView,
        smimeType: getSmimeTypeForConversation(tableConversationRelation.itemClasses),
        sendersOrRecipients,
        shouldShowTwisty: conversationItem.globalMessageCount > 1,
        shouldShowRSVP: tableConversationRelation.shouldShowRSVP,
        isInNotesFolder: isItemInNotesFolder,
        isInOutboxFolder: isItemInOutboxFolder,
        isInArchiveFolder: isItemInArchiveFolder,
        isInJunkFolder: isItemInJunkFoler,
        shouldShowSenderOnTop,
        shouldShowExternalLabel: !!conversationItem.hasExternalEmails && externalInOutlookEnabled(mailboxInfo),
        shouldSuggestUnsubscribe,
        hasSharepointLink: conversationItem.hasSharepointLink,
        canArchive: isArchiveSupported(tableView, mailboxInfo, folderName),
        canPin: canPin(tableView, mailListTableProps.supportsPinning, parentFolderId),
        isInGroup: isGroupItem,
        mailboxGuids,
        conditionalFormattingModifications: isGlobalFeatureEnabled("mon-mail-list-focus-computed") &&
            conditionalFormattingModifications.length === 0
            ? emptyConditionalFormattingModifications
            : conditionalFormattingModifications,
        meetingStartTime,
        copilotInboxScore,
        mailboxInfo,
        isReminderSet: !!conversationItem.conversationFlagReminderIsSet,
        isScheduledSend,
        isProactiveDraft: isProactiveDraftItem,
        messageSizeBytes: conversationItem.size,
        flagDueDate,
    };
}

function getSendersOrRecipients(recipients: string[], uniqueSenders: string[], lastSenderSMTP: string, isItemInSentItemsFolder: boolean, isItemInDraftsFolder: boolean, isItemInOutboxFolder: boolean, isSearchNotInDraftOrSentItemFolder: boolean, tableQuery: TableQuery): {
    sendersOrRecipients: string;
    singleLastSenderSMTP: string | null;
} {
    let sendersOrRecipients;
    let singleLastSenderSMTP;
    const { scenarioType } = tableQuery as MailFolderTableQuery;
    if ((isItemInSentItemsFolder || isItemInDraftsFolder || isItemInOutboxFolder) &&
        !isSearchNotInDraftOrSentItemFolder &&
        !isSearchFolderScenario(scenarioType)) {
        // Show recipients for conversations belonging to drafts, sentitems and outbox folders
        // EXCEPT when we are in a search scenario since we want the preview text, name, and circle persona to match
        sendersOrRecipients = recipients && recipients.join("; ");
        singleLastSenderSMTP = null;
    }
    else {
        sendersOrRecipients =
            uniqueSenders && uniqueSenders.map(getSenderNameForDisplay).join("; ");
        
        // Show tooltip text when there is single sender
        singleLastSenderSMTP =
            uniqueSenders && uniqueSenders.length === 1
                ? getSenderNameForDisplay(lastSenderSMTP)
                : null;
    }
    
    // If there are no senders or no recipients add a default string [Unknown]
    // For an item in a drafts folder user may not have defined any recipients
    // For items (in both sent & drafts) that do not have recipients in To: and
    // have recipients in Cc:/Bcc: we do not receive any recipients today from server.
    if (!sendersOrRecipients) {
        sendersOrRecipients = isItemInDraftsFolder ? "" : loc(unknownSenderOrRecipient);
    }
    
    return { sendersOrRecipients, singleLastSenderSMTP };
}

// Helper function to normalize and compare date times for isSnoozed calculation. This function is
// required to normalize times returned by search results with millisecond. For the present we will
// simply ignore milliseconds and assume the two times are equal when in search.
function isTimestampEqual(time1: string, time2: string) {
    return new Date(time1).setMilliseconds(0) == new Date(time2).setMilliseconds(0);
}

// Return item's return time IF the item has a defined return time. Otherwise, if we know
// item is snoozed and the return time does not exist (ex: in inbox), we will rely on RoRT.
function getItemReturnTime(item: ClientItem, isSnoozed: boolean) {
    if (item.ReturnTime) {
        return item.ReturnTime;
    }
    
    if (isSnoozed && item.ReceivedOrRenewTime) {
        return item.ReceivedOrRenewTime;
    }
    
    return null;
}

// Return TCR's return time IF the TCR has a defined return time. Otherwise, if we know
// the TCR was snoozed and the return time does not exist (ex: in inbox), we will rely on LDoRT.
function getTableConversationRelationReturnTime(tableConversationRelation: MailRowDataPropertyGetter.TableViewConversationRelation, isSnoozed: boolean) {
    return tableConversationRelation.returnTime
        ? tableConversationRelation.returnTime
        : isSnoozed
            ? tableConversationRelation.lastDeliveryOrRenewTimeStamp
            : null;
}

// Returns the timestamp shown in the row's date column. For Drafts this prefers the
// service-stamped LastMeaningfulDraftSaveTime (falling back to LastModifiedTime) when the
// tri-draftsMeaningfulSaveTime flight is on, so the displayed date reflects the user's
// genuine save rather than bot activity; with the flight off it stays on LastModifiedTime
// (pre-feature behavior). For all other folders it is the delivery time (DateTimeReceived).
function getDraftAwareDisplayTimestamp(item: ClientItem, isDraftsFolder: boolean, useMeaningfulSaveTime: boolean) {
    if (isDraftsFolder) {
        if (useMeaningfulSaveTime && item.LastMeaningfulDraftSaveTime) {
            return item.LastMeaningfulDraftSaveTime;
        }
        
        if (item.LastModifiedTime) {
            return item.LastModifiedTime;
        }
    }
    
    if (item.DateTimeReceived) {
        return item.DateTimeReceived;
    }
    
    return "";
}

// If user has selected a category node , we want to hide that category in the category list.
// This function removes a category given a category list.
function getCategoriesToShow(tableView: TableView, categories: string[] | undefined) {
    if (!categories) {
        return [];
    }
    
    const categoryName = getUserCategoryName(tableView);
    
    if (!categoryName) {
        return categories;
    }
    
    const filteredCategories = [...categories];
    const categoryNameIndex = filteredCategories.indexOf(categoryName);
    
    if (categoryNameIndex > -1) {
        filteredCategories.splice(categoryNameIndex, 1);
    }
    
    return filteredCategories;
}

/**
 * Determine, according to the rowKey, if for certain message list item
 * unsubscribe should be suggested.
 * @param rowId Message rowId
 * @param tableView Table view
 * @param mailboxInfo MailboxInfo of the selected account
 *
 * @returns Whether the item should show suggested unsubscribe component
 */
function shouldShowSuggestedUnsubscribe(rowId: string, tableView: TableView, mailboxInfo: MailboxInfo): boolean {
    return (isConsumer(undefined /* smtpAddress */, mailboxInfo) &&
        folderIdToName(tableView.tableQuery.folderId) == "inbox" &&
        isSuggestedUnsubscribeItem(rowId, mailboxInfo));
}

function isItemOfAppointmentType(itemClass: string) {
    return (itemClass && itemClass.indexOf("IPM.Appointment") == 0 // check if null as well
    );
}

/**
 * This function determines if an item can be pinned. This check is required
 * on an item level for scenarios in which a subset of the rows can be pinned
 * and others cannot be (i.e. search).
 */
function canPin(tableView: TableView, tableSupportsPinning: boolean, parentFolderId: string): boolean {
    /**
     * If the tableView isn't for search or copilot results, then we can rely on whether or not
     * the table supports pinning. Additionally, we can early return if the
     * table itself doesn't support pinning at all.
     */
    if ((tableView.tableQuery.type !== 1 &&
        !isCopilotResultsTable(tableView.tableQuery)) ||
        !tableSupportsPinning) {
        return tableSupportsPinning;
    }
    
    // Folders where pin is not supported
    const restrictedFoldersForPin = [
        "drafts",
        "clutter",
        "junkemail",
        "deleteditems",
        "archive",
        "notes",
        "recoverableitemsdeletions", // PRIMARY_DUMPSTER_DISTINGUISHED_ID
        "archiverecoverableitemsdeletions", // ARCHIVE_DUMPSTER_DISTINGUISHED_ID
    ];
    
    const parentFolderName = parentFolderId && folderIdToName(parentFolderId, true /* shouldSearchShared */);
    for (const restrictedFolder of restrictedFoldersForPin) {
        if (parentFolderName === restrictedFolder) {
            return false;
        }
    }
    
    return true;
}

function shouldShowCopilotPrioritizeHeadline(copilotInboxScore: number | undefined, copilotInboxHeadline: string | undefined, tableView: TableView, mailboxInfo: MailboxInfo): boolean {
    if (!copilotInboxScore || !copilotInboxHeadline) {
        return false;
    }
    
    const { applyLowPriorityLabel, prioritizationEnabled, replacePreviewWithMicroSummary } = getCopilotPrioritizeSettings(mailboxInfo);
    
    if (!replacePreviewWithMicroSummary) {
        return false;
    }
    
    if (!(prioritizationEnabled && isPrioritizeOnDeliveryEnabled(mailboxInfo))) {
        return false;
    }
    
    const sort = (tableView.tableQuery as MailFolderTableQuery).sortBy?.sortColumn;
    const isPrioritySort = sort === 16;
    
    return (isCopilotHighPriority(copilotInboxScore) ||
        ((applyLowPriorityLabel || isPrioritySort) && isCopilotLowPriority(copilotInboxScore)));
}

function convertToMB(sizeInBytes: number | null): number | null {
    if (sizeInBytes === null) {
        return null;
    }
    return Math.round(sizeInBytes / (1024 * 1024));
}

export default getMailListItemDataProps;
